// client/src/pages/AdminPollsPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../authentication/AuthContext';
import { pollsService } from '../services/pollsService';
import { 
  Box, Typography, Paper, Button, TextField, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Dialog, DialogTitle, 
  DialogContent, DialogActions, CircularProgress, IconButton,
  FormControl, InputLabel, Select, MenuItem, Chip, Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../styles/Theme.js';
import '../styles/PollsPage.css';

function AdminPollsPage() {
  const { user } = useAuth();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [newPollQuestion, setNewPollQuestion] = useState('');
  const [pollType, setPollType] = useState('multiple-choice');
  const [options, setOptions] = useState(['']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // if (!user || !user.attributes?.['custom:isAdmin']) {
    //   // Redirect non-admin users
    //   window.location.href = '/';
    //   return;
    // }
    
    fetchPolls();
  }, [user]);

  const fetchPolls = async () => {
    try {
      setLoading(true);
      const pollsData = await pollsService.getAllPolls();
      setPolls(pollsData || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching polls:', err);
      setError('Failed to load polls. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setNewPollQuestion('');
    setPollType('multiple-choice');
    setOptions(['', '']);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  const handleRemoveOption = (index) => {
    if (options.length <= 2) {
      return; // Maintain at least 2 options
    }
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
  };

  const handlePollTypeChange = (event) => {
    const newType = event.target.value;
    setPollType(newType);
    
    // Initialize options based on poll type
    if (newType === 'yes-no') {
      setOptions(['Yes', 'No']);
    } else if (newType === 'rating') {
      setOptions(['1', '2', '3', '4', '5']);
    } else if (newType === 'multiple-choice' && options.length < 2) {
      setOptions(['', '']);
    }
  };

  const handleSubmitPoll = async () => {
    if (!newPollQuestion.trim()) return;
    
    // Validate options for multiple-choice polls
    if (pollType === 'multiple-choice') {
      const validOptions = options.filter(opt => opt.trim() !== '');
      if (validOptions.length < 2) {
        setError('Please provide at least 2 options for the poll');
        return;
      }
      
      // Remove empty options
      const cleanedOptions = options.filter(opt => opt.trim() !== '');
      setOptions(cleanedOptions);
    }
    
    try {
      setSubmitting(true);
      
      // Use pollType-specific options
      let pollOptions;
      if (pollType === 'yes-no') {
        pollOptions = ['Yes', 'No'];
      } else if (pollType === 'rating') {
        pollOptions = ['1', '2', '3', '4', '5'];
      } else {
        pollOptions = options.filter(opt => opt.trim() !== '');
      }
      
      await pollsService.createPoll(newPollQuestion, pollOptions, pollType);
      setNewPollQuestion('');
      handleCloseDialog();
      fetchPolls();
    } catch (err) {
      // console.error('Error creating poll:', err);
      setError('Failed to create poll. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePoll = async (pollId) => {
    if (!window.confirm('Are you sure you want to delete this poll?')) return;
    
    try {
      await pollsService.deletePoll(pollId);
      fetchPolls();
    } catch (err) {
      // console.error('Error deleting poll:', err);
      setError('Failed to delete poll. Please try again.');
    }
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      <div className="polls-page">
        <Box sx={{ padding: 4, maxWidth: '1200px', margin: '0 auto' }}>
          <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
            Manage Polls
          </Typography>
          
          <Box sx={{ mb: 4 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleOpenDialog}
              sx={{ mr: 2 }}
            >
              Create New Poll
            </Button>
            <Button 
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchPolls}
            >
              Refresh
            </Button>
          </Box>
          
          {error && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#ffebee' }}>
              <Typography color="error">{error}</Typography>
            </Paper>
          )}
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : polls.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6">No polls available</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Create your first poll to get started!
              </Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Question</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Options</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Responses</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {polls.map((poll) => (
                    <TableRow key={poll.pollId}>
                      <TableCell>{poll.question}</TableCell>
                      <TableCell>{poll.pollType || 'text'}</TableCell>
                      <TableCell>
                        {poll.options && poll.options.length > 0 ? (
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {poll.options.map((option, i) => (
                              <Chip key={i} label={option} size="small" />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">None</Typography>
                        )}
                      </TableCell>
                      <TableCell>{new Date(poll.createdAt * 1000).toLocaleDateString()}</TableCell>
                      <TableCell>{poll.status}</TableCell>
                      <TableCell>{Object.keys(poll.responses || {}).length}</TableCell>
                      <TableCell>
                        <IconButton 
                          color="error" 
                          onClick={() => handleDeletePoll(poll.pollId)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
        
        {/* New Poll Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>Create New Poll</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                autoFocus
                label="Poll Question"
                type="text"
                fullWidth
                value={newPollQuestion}
                onChange={(e) => setNewPollQuestion(e.target.value)}
                placeholder="What would you like to ask our personas?"
                variant="outlined"
                disabled={submitting}
                sx={{ mb: 3 }}
              />
              
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Poll Type</InputLabel>
                <Select
                  value={pollType}
                  onChange={handlePollTypeChange}
                  label="Poll Type"
                  disabled={submitting}
                >
                  <MenuItem value="multiple-choice">Multiple Choice</MenuItem>
                  <MenuItem value="yes-no">Yes/No</MenuItem>
                  <MenuItem value="rating">Rating (1-5)</MenuItem>
                </Select>
              </FormControl>
              
              {pollType === 'multiple-choice' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>Options:</Typography>
                  
                  {options.map((option, index) => (
                    <Box key={index} sx={{ display: 'flex', mb: 1 }}>
                      <TextField
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        fullWidth
                        variant="outlined"
                        size="small"
                        disabled={submitting}
                      />
                      <IconButton 
                        color="error" 
                        onClick={() => handleRemoveOption(index)}
                        disabled={options.length <= 2 || submitting}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  
                  <Button 
                    startIcon={<AddIcon />} 
                    onClick={handleAddOption}
                    disabled={submitting}
                    sx={{ mt: 1 }}
                  >
                    Add Option
                  </Button>
                </Box>
              )}
              
              {pollType === 'yes-no' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    This will create a simple Yes/No poll
                  </Typography>
                </Box>
              )}
              
              {pollType === 'rating' && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    This will create a rating poll with options 1-5
                  </Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={submitting}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitPoll} 
              color="primary" 
              variant="contained"
              disabled={submitting || !newPollQuestion.trim() || 
                (pollType === 'multiple-choice' && options.filter(o => o.trim()).length < 2)}
            >
              {submitting ? 'Creating...' : 'Create Poll'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </ThemeProvider>
  );
}

export default AdminPollsPage;